/**
 * Thread Tour Definitions
 *
 * Each tour walks users through a specific capability of the Thread
 * progressive-disclosure layout. The `focusPanel` field tells the
 * OnboardingProvider to call setOpenPanel() before highlighting.
 */

import type { TourStep, OnboardingConfig, Challenge } from './types';
import { createPresenceChallenge } from './challenges';

// ---------------------------------------------------------------------------
// Challenges
// ---------------------------------------------------------------------------

const challenges: Record<string, Challenge> = {
  'open-explore': createPresenceChallenge({
    id: 'open-explore',
    instruction: 'Click the "Explore" button in the toolbar to open the side panel.',
    hint: 'Look for the Explore button in the top-right of the toolbar.',
    selector: '[data-tour="slide-panel"]',
  }),
  'click-dag-node': createPresenceChallenge({
    id: 'click-dag-node',
    instruction: 'Click any node in the DAG to select it.',
    hint: 'Colored nodes represent steps in your simulation pipeline.',
    selector: '.react-flow__node.selected, [data-tour="dag-panel"] .node-selected-flash',
  }),
  'open-data-tab': createPresenceChallenge({
    id: 'open-data-tab',
    instruction: 'Click the "Data" tab in the side panel.',
    hint: 'The Data tab shows the run table with all simulation results.',
    selector: '[data-tour="panel-data"]',
  }),
  'sref-branch-created': createPresenceChallenge({
    id: 'sref-branch-created',
    instruction: 'Create a new branch to investigate.',
    hint: 'Use the branch selector in the toolbar — click "New Branch".',
    selector: '[data-tour="branch-selector"]',
  }),
};

// ---------------------------------------------------------------------------
// Welcome Tour — overview of the full Thread workspace
// ---------------------------------------------------------------------------

const welcomeTour: TourStep[] = [
  {
    popover: {
      title: 'Welcome to Thread',
      description:
        'Thread is your provenance-aware workspace for aerospace simulation. ' +
        'Every geometry, mesh, and solver run is tracked as a node in a directed acyclic graph (DAG).',
    },
  },
  {
    element: '[data-tour="dag-panel"]',
    popover: {
      title: 'The Provenance DAG',
      description:
        'This is the heart of Thread. Each node represents a step in your simulation pipeline — ' +
        'geometries, meshes, and solver runs. Edges show how they connect.',
      side: 'right',
    },
  },
  {
    element: '[data-tour="explore-toggle"]',
    popover: {
      title: 'Progressive Disclosure',
      description:
        'Thread starts focused on the DAG. Click "Explore" to reveal the side panel with ' +
        'data tables, plot builders, and more. Click again to hide it.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="slide-panel"]',
    focusPanel: 'data',
    popover: {
      title: 'The Slide Panel',
      description:
        'The side panel has tabs for Data Explorer, Plot Builder, Saved Plots, ' +
        'and more. Select a node to see its details and comment thread. Drag any tab onto the main area to promote it full-width.',
      side: 'left',
    },
  },
  {
    element: '[data-tour="tab-bar"]',
    focusPanel: 'data',
    popover: {
      title: 'Drag to Promote',
      description:
        'Tabs are draggable. Drag one to the main pane to give it more space. ' +
        'The DAG moves to the sidebar. Drag it back to restore.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="branch-selector"]',
    popover: {
      title: 'Version Control',
      description:
        'Thread tracks branches just like git. Switch between branches to explore ' +
        'different configurations of your simulation.',
      side: 'bottom',
    },
  },
  {
    popover: {
      title: 'Ready to Explore',
      description:
        'That\'s the basics! Use the Explore toggle to open the side panel and dig into your data. ' +
        'You can restart this tour any time from the help menu.',
    },
  },
];

// ---------------------------------------------------------------------------
// Data Explorer Tour
// ---------------------------------------------------------------------------

const dataExplorerTour: TourStep[] = [
  {
    element: '[data-tour="explore-toggle"]',
    popover: {
      title: 'Open the Data Explorer',
      description: 'Start by opening the Explore panel.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="panel-data"]',
    focusPanel: 'data',
    popover: {
      title: 'The Run Table',
      description:
        'Every solver run is a row. Columns show quantities like Mach number, angle of attack, ' +
        'and convergence metrics. Click a row to select it in the DAG.',
      side: 'left',
    },
  },
  {
    element: '[data-tour="correlogram"]',
    focusPanel: 'data',
    popover: {
      title: 'Correlogram',
      description:
        'The correlogram shows scatter plots for every pair of numeric columns. ' +
        'Click a cell to open it in the Plot Builder.',
      side: 'left',
    },
  },
  {
    popover: {
      title: 'Cross-Panel Selection',
      description:
        'When you select rows in the table or nodes in the DAG, the selection ' +
        'syncs everywhere — DAG highlights, table row focus, and plot point emphasis all update together.',
    },
  },
];

// ---------------------------------------------------------------------------
// DAG Navigation Tour
// ---------------------------------------------------------------------------

const dagNavigationTour: TourStep[] = [
  {
    element: '[data-tour="dag-panel"]',
    popover: {
      title: 'Navigate the DAG',
      description:
        'Pan with click-drag on the background. Zoom with scroll wheel. ' +
        'The minimap in the corner shows your viewport.',
      side: 'right',
    },
  },
  {
    element: '[data-tour="dag-controls"]',
    popover: {
      title: 'DAG Controls',
      description:
        'Use these buttons to toggle sweep grouping (collapses parameter sweeps) ' +
        'and show/hide quantity details on each node.',
      side: 'left',
    },
  },
  {
    element: '[data-tour="dag-panel"]',
    popover: {
      title: 'Node Interactions',
      description:
        'Click a node to select it and see its details. ' +
        'Double-click to expand inline quantity details. ' +
        'Clicking a mesh node reveals actions (refine, re-mesh).',
      side: 'right',
    },
  },
  {
    element: '[data-tour="branch-selector"]',
    popover: {
      title: 'Branch Switching',
      description:
        'The DAG updates when you switch branches. Each branch can have ' +
        'different simulation configurations and results.',
      side: 'bottom',
    },
  },
];

// ---------------------------------------------------------------------------
// Comments & Collaboration Tour
// ---------------------------------------------------------------------------

const commentsTour: TourStep[] = [
  {
    popover: {
      title: 'Comments & Collaboration',
      description:
        'Thread now supports threaded, branch-scoped comments on every artifact in your simulation. ' +
        'Comments follow the provenance graph — they migrate on merge and are soft-deleted on branch removal.',
    },
  },
  {
    element: '[data-tour="dag-panel"]',
    popover: {
      title: 'Comment Badges on the DAG',
      description:
        'Nodes with unresolved comments show an orange badge with the count. ' +
        'This gives your team at-a-glance visibility into where discussions are happening.',
      side: 'right',
    },
  },
  {
    element: '[data-tour="conversations-panel"]',
    popover: {
      title: 'Conversations Tab',
      description:
        'The "Chat" tab is your dedicated space for discussions. Select a node to see its threads, ' +
        'or view project-level conversations when nothing is selected. You can also right-click any node and choose "Start a conversation".',
      side: 'left',
    },
  },
  {
    element: '[data-tour="branch-selector"]',
    popover: {
      title: 'Branch-Scoped Conversations',
      description:
        'Comments live on the branch where they were created. When you merge a branch, ' +
        'its comments migrate to the target. This keeps design reviews tied to the correct version of the data.',
      side: 'bottom',
    },
  },
  {
    popover: {
      title: 'Spatial Anchoring',
      description:
        'Comments can be pinned to specific locations within artifacts — a data point on a plot, ' +
        'a point on a 3D surface, or a cell in a table. Anchors use data coordinates so they survive resizing and re-rendering.',
    },
  },
  {
    popover: {
      title: 'Context Snapshots',
      description:
        'Every comment captures a snapshot of the target artifact at the time of writing. ' +
        'This ensures that reviewers can always see what the commenter was looking at — critical for audit trails.',
    },
  },
  {
    popover: {
      title: 'Ready to Collaborate',
      description:
        'Right-click any node and choose "Start a conversation" to jump straight into the Chat tab. ' +
        'You can restart this tour any time from the help menu.',
    },
  },
];

// ---------------------------------------------------------------------------
// S_ref Mystery Tour — guided investigation of wrong reference area
//
// Real Flow360 data: Simple Airplane, 9-pt alpha sweep.
// S_ref = 11.01 m² (single-wing planform) instead of 22.02 m² (full wing).
// CL values are 2× too high. The detection method finds the correct value.
//
// Story arc: plot → slope → DAG → diagnosis → detection → fix
// ---------------------------------------------------------------------------

const srefMysteryTour: TourStep[] = [
  // --- ACT 1: Start with the saved CL-alpha plot ---
  {
    focusPanel: 'saved-plots',
    popover: {
      title: 'A Lift Curve from Flow360',
      description:
        'This project is an alpha sweep of a simple airplane, solved in Flow360. ' +
        'The saved plot shows CL vs \u03b1 from 9 converged cases.\n\n' +
        'Look at the curve \u2014 CL reaches 1.78 at \u03b1 = 14\u00b0. ' +
        'That seems high for this geometry, but it\u2019s not immediately obvious something is wrong.',
      side: 'left',
    },
  },
  {
    focusPanel: 'saved-plots',
    popover: {
      title: 'The Lift-Curve Slope',
      description:
        'The slope of this line is 0.12/deg. For a wing with AR \u2248 4.6, theory predicts ' +
        'CL\u03b1 \u2248 0.06/deg. We\u2019re seeing exactly double the expected lift-curve slope. ' +
        'Every coefficient is scaled up by the same factor. Why?',
      side: 'left',
    },
    stopPoint: true,
  },

  // --- ACT 2: Zoom out to the DAG — trace provenance ---
  {
    element: '[data-tour="dag-panel"]',
    focusPanel: 'close',
    popover: {
      title: 'Where Did These Numbers Come From?',
      description:
        'Thread tracks the provenance of every simulation result. This is the DAG \u2014 ' +
        'geometry, meshes, solver runs, and the reference quantities that govern them, all connected.',
      side: 'right',
    },
  },
  {
    element: '[data-tour="dag-panel"]',
    popover: {
      title: 'The Provenance Chain',
      description:
        'Geometry \u2192 surface mesh \u2192 volume mesh \u2192 solver runs. ' +
        'Notice the orange group: Reference Quantities. These control how ' +
        'forces are non-dimensionalised into coefficients. CL = L / (q \u00b7 S_ref). ' +
        'If S_ref is wrong, every coefficient is wrong by the same factor.',
      side: 'right',
    },
    stopPoint: true,
  },

  // --- ACT 3: Diagnose the root cause ---
  {
    element: '[data-tour="run-table"]',
    focusPanel: 'data',
    popover: {
      title: 'S_ref = 11.01 m\u00b2',
      description:
        'Check the reference area in the data panel: S_ref = 11.01 m\u00b2. ' +
        'That\u2019s the planform area of a single wing \u2014 someone used one side only ' +
        'instead of the full wing reference area. Halving S_ref doubles every coefficient.',
      side: 'left',
    },
  },
  {
    element: '[data-tour="conversations-panel"]',
    focusPanel: 'conversations',
    popover: {
      title: 'Your Colleague Noticed Too',
      description:
        'K. Patel flagged it: "The lift-curve slope is 0.12/deg \u2014 about 2\u00d7 what I\u2019d ' +
        'expect for this wing. Did someone use the single-wing planform area for S_ref?" ' +
        'Thread keeps conversations anchored to the data.',
      side: 'left',
    },
    stopPoint: true,
  },

  // --- ACT 4: Use wing area detection to find the correct value ---
  {
    element: '[data-tour="dag-panel"]',
    focusPanel: 'close',
    popover: {
      title: 'Detect S_ref from Geometry',
      description:
        'Thread can derive reference quantities directly from the OML via planform projection. ' +
        'The wing area detection analysed the Simple Airplane geometry and found the full ' +
        'trapezoidal planform area: S_ref = 22.02 m\u00b2 \u2014 exactly 2\u00d7 the current value. ' +
        'That explains the 2\u00d7 scaling on every coefficient.',
      side: 'right',
    },
    stopPoint: true,
  },

  // --- ACT 5: Fix it the Thread way ---
  {
    element: '[data-tour="branch-selector"]',
    challengeId: 'sref-branch-created',
    popover: {
      title: 'Don\u2019t Edit Main',
      description:
        'Reference quantities are governed in Thread. Create a branch to investigate \u2014 ' +
        'version control for simulation data, not just code.',
      side: 'bottom',
    },
  },
  {
    popover: {
      title: 'Apply & Commit',
      description:
        'Update reference quantities in the Configuration panel (Reference Quantities tab) if needed. ' +
        'Then commit and request a merge to main \u2014 the project owner is notified for approval.\n\n' +
        'Every step \u2014 the wrong single-wing value, the geometry detection, the fix, ' +
        'and the approval \u2014 is tracked in Thread\u2019s provenance graph.',
    },
  },
];

// ---------------------------------------------------------------------------
// Export the full config
// ---------------------------------------------------------------------------

export const threadOnboardingConfig: OnboardingConfig = {
  tours: {
    welcome: welcomeTour,
    'data-explorer': dataExplorerTour,
    'dag-navigation': dagNavigationTour,
    'comments': commentsTour,
    'sref-mystery': srefMysteryTour,
  },
  challenges,
  storagePrefix: 'thread-onboarding',
};
